/**
 * useOrgQuery — the sanctioned way to load an org-scoped list.
 *
 * Karr screens historically fetched with a manual `useEffect` + `useState`, which
 * has two costs the founder felt on the Sales list:
 *
 *   1. No cache. Every visit re-runs the full network round-trip, so the screen
 *      shows a skeleton and "takes some time to load" each time you click it.
 *   2. Silent truncation. A plain `.select()` is capped by PostgREST at ~1000
 *      rows, so a multi-year book (invoices back to 2021) only ever returned the
 *      newest ~1000 — older documents, and any total computed from them, were
 *      silently missing.
 *
 * This hook fixes both, uniformly:
 *
 *   • It wraps TanStack Query (already provided in main.tsx). Re-navigating to a
 *     screen renders instantly from cache while a background refetch revalidates
 *     ("stale-while-revalidate") — the app feels realtime instead of reloading.
 *   • `placeholderData: keepPreviousData` keeps the current rows on screen during
 *     that refetch, so only the FIRST load per org shows a skeleton.
 *   • It re-uses the app-wide live tick (tab focus + Supabase realtime, see
 *     useLiveRefresh) to invalidate ITS query — one freshness mechanism, no
 *     per-screen refetch plumbing.
 *
 * Pair it with `fetchAllRows` (below) in the fetcher to defeat the 1000-row cap.
 *
 * @since 3.1.0
 */
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { useLiveTick } from './useLiveRefresh';

export interface AllRows<T> {
    rows: T[];
    /**
     * True when the safety cap (`max`) was reached and OLDER rows were not
     * loaded. Never silently drop data — surface a "showing most recent N"
     * banner so the number on screen is honest.
     */
    truncated: boolean;
}

// Loosely typed so any Supabase query builder (incl. embedded-relation selects,
// which infer awkward array shapes) is assignable. The caller's generic `T`
// describes the OUTPUT row, not the builder — batches are cast to T[] internally.
type RangedResult = PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>;

/** PostgREST returns at most ~1000 rows per request; page in blocks of this size. */
const DEFAULT_PAGE = 1000;
/** Hard ceiling so a pathological book can't pull tens of MB into the browser. */
const DEFAULT_MAX = 20_000;

/**
 * Page through a Supabase query with `.range()` until every row is fetched,
 * defeating the ~1000-row PostgREST cap. Pass a factory that applies `.range()`
 * to a FRESH query each call (Supabase builders are single-shot):
 *
 * ```ts
 * const { rows, truncated } = await fetchAllRows<Invoice>((from, to) =>
 *   supabase.from('transactions')
 *     .select('id, number, date, total, status')
 *     .eq('org_id', orgId).eq('type', 'invoice')
 *     .order('date', { ascending: false })
 *     .range(from, to),
 * );
 * ```
 */
export async function fetchAllRows<T>(
    rangedQuery: (from: number, to: number) => RangedResult,
    opts: { pageSize?: number; max?: number } = {},
): Promise<AllRows<T>> {
    const pageSize = opts.pageSize ?? DEFAULT_PAGE;
    const max = opts.max ?? DEFAULT_MAX;
    const rows: T[] = [];
    let from = 0;
    for (;;) {
        const { data, error } = await rangedQuery(from, from + pageSize - 1);
        if (error) throw new Error(error.message);
        const batch = (data ?? []) as T[];
        rows.push(...batch);
        // A short page means we've reached the end of the result set.
        if (batch.length < pageSize) return { rows, truncated: false };
        // Safety cap hit — stop and let the caller flag the truncation.
        if (rows.length >= max) return { rows, truncated: true };
        from += pageSize;
    }
}

/**
 * Cached, org-scoped query. `key` identifies the logical dataset (e.g.
 * `['invoices']` or `['invoices', statusFilter]`); the org id is prepended
 * automatically, so switching orgs is a clean cache miss (skeleton), and every
 * live tick is a silent background refetch.
 *
 * Returns the raw TanStack `UseQueryResult`. Typical usage:
 *   const q = useOrgQuery(['invoices'], (orgId) => fetchAllRows(...));
 *   const invoices = q.data?.rows ?? EMPTY;   // stable-ref empty fallback
 *   const loading  = q.isPending;             // skeleton on first load only
 *   q.refetch();                              // after a post/save mutation
 *
 * `live: false` (linegrid-2): the live tick does not re-read it. For a form's
 * lists, which must not be fetched again while the person types; they go
 * stale after `staleTime` and are read again on the next open.
 *
 * `pollWhile` (ype-1): read again every `ms` while the data says something is
 * still happening, and not at all once it says nothing is, so a screen that
 * waits on nobody costs no call (docs/YOUR_PLACE_EASY_SPEC.md §5.5).
 */
export function useOrgQuery<T>(
    key: readonly (string | number | boolean | null | undefined)[],
    fetcher: (orgId: string) => Promise<T>,
    opts: { enabled?: boolean; staleTime?: number; live?: boolean; pollWhile?: { ms: number; when: (data: T | undefined) => boolean } } = {},
) {
    // Match the app-wide org resolution (currentOrg, falling back to the first
    // membership before `initialize()` has set currentOrg) so a screen's cached
    // query enables at the same moment as its other fetches.
    const currentOrg = useAuth((s) => s.currentOrg);
    const orgs = useAuth((s) => s.orgs);
    const orgId = (currentOrg ?? orgs?.[0])?.id ?? null;
    const tick = useLiveTick();
    const queryClient = useQueryClient();
    const enabled = (opts.enabled ?? true) && !!orgId;
    const live = opts.live ?? true;
    const queryKey: QueryKey = ['org', orgId, ...key];

    // No `placeholderData: keepPreviousData` on purpose: a same-org refetch
    // (live tick, post-save) already keeps its cached rows on screen while
    // revalidating, so those are silent. But an ORG SWITCH is a new query key —
    // we WANT its skeleton then, not the previous org's rows bleeding through.
    const query = useQuery({
        queryKey,
        queryFn: () => fetcher(orgId as string),
        enabled,
        staleTime: opts.staleTime ?? 30_000,
        refetchInterval: opts.pollWhile ? (q) => (opts.pollWhile!.when(q.state.data as T | undefined) ? opts.pollWhile!.ms : false) : undefined,
    });

    // The app-wide live tick (tab focus + realtime; see useLiveRefresh) becomes a
    // silent background refetch of this screen's data. tick === 0 is the initial
    // value — the mount fetch already covers it, so don't double-fetch.
    const keyStr = JSON.stringify(queryKey);
    useEffect(() => {
        if (!enabled || !live || tick === 0) return;
        void queryClient.invalidateQueries({ queryKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick, keyStr]);

    return query;
}
